import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/bootstrap/TemporaryReactstrapCompatibilityLayer';

import AuthContext from '../../../contexts/auth/AuthContext';
import ContainersApiService from '../../../services/apiServices/ContainersApiService';
import UIContext from '../../../contexts/ui/UIContext';
import StatusBarButton from '../../layout/StatusBarButton';
import ProcessContext from '../../../contexts/process/ProcessContext';
import ContainerCard from './Card';
import { HELP_DESK_LINK } from '../../../constants/helpDesk';
import ConfirmDeleteModal from '../seedTypes/ConfirmDeleteModal';

const ContainerManagement = () => {
  const navigate = useNavigate();
  const { setInfo, addNotification } = useContext(UIContext);
  const { currentTenant, accessibleContainers, loadAndSetAccessibleContainersAsync } = useContext(AuthContext);
  const { notifyWarning } = useContext(ProcessContext);

  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setInfo('Container Management');
  }, []);

  const clickHandler = () => {
    navigate('/management/containers/create');
  }

  const requestDelete = (id) => {
    setPendingDeleteId(id);
    setShowConfirm(true);
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setPendingDeleteId(null);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    setShowConfirm(false);
    setDeleting(true);
    try {
      await ContainersApiService.deleteContainer(currentTenant?.id, pendingDeleteId);
      addNotification('Successfully deleted container.', 'success');
      await loadAndSetAccessibleContainersAsync(currentTenant);
    } catch (error) {
      if (error && error.message === 'Not found.') {
        notifyWarning(
          <>We have encountered an issue while deleting the container. Could you please logout, sign back in, and try again? If the error still exists, please contact the support team {HELP_DESK_LINK}.</>,
          'error'
        );
      } else {
        notifyWarning(
          <>We have encountered an issue on our side while deleting the container. Please contact support team {HELP_DESK_LINK}.</>,
          'error'
        );
      }
    } finally {
      setDeleting(false);
      setPendingDeleteId(null);
    }
  };

  return (
    <>
      <div className='container-management-container'>
        <div className='container-list-overview'>
          <Card className='create-container-card' onClick={clickHandler}>
            <h1 className='add-seed-symbol'><i className='fas fa-plus'></i></h1>
          </Card>

          {accessibleContainers &&
            accessibleContainers
              .slice()
              .sort((a, b) => a.name.toUpperCase().localeCompare(b.name.toUpperCase()))
              .map((container) => (
                <ContainerCard
                  key={container.id}
                  container={container}
                  deleteHandler={() => requestDelete(container.id)}
                />
              ))}
        </div>
      </div>

      <StatusBarButton
        icon='fair fai-circle-check'
        label='Done'
        type='inline'
        clickHandler={() => navigate('/')}
        statusSlot={3}
      />

      <ConfirmDeleteModal
        show={showConfirm}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        header="DELETE CONTAINER"
        message={
          <>
            Deleting the container may take up to several minutes. Please don't
            navigate away or close the app.
            <br />
            Are you sure you want to delete it?
          </>
        }
      />

      <ConfirmDeleteModal
        show={deleting}
        hideActions
        header="Container deletion in progress..."
        message={
          <>
            <p>Deleting the container may take up to several minutes. Please don't navigate away or close the app.</p>
            <i className='fas fa-cog fa-spin' />
          </>
        }
      />
    </>
  );
}

export default ContainerManagement;
